import { defineStore } from 'pinia';
import { apiCall } from '@/utils/globalFunctions';
import store from '@/store/store';

// Helper functions
function getPersistedState(storageKey) {
  try {
    const stored = localStorage.getItem(storageKey);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.warn('Failed to parse stored devices state', error);
    return {};
  }
}

function setPersistedState(storageKey, state) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(state));
  } catch (error) {
    console.warn('Failed to persist devices state', error);
  }
}

export const useDeviceStore = defineStore('devices', {
  state: () => {
    const userId = store.state.user?.user_id || null;
    const storageKey = userId ? `deviceStore_${userId}` : 'deviceStore';
    const persistedState = getPersistedState(storageKey);
    return {
      userId,
      devices: persistedState.devices || [],
      loading: false,
      error: null,
    };
  },

  getters: {
    filteredDevices: (state) => state.devices.map(device => ({
        ...device,
        is_active: Boolean(Number(device.is_active)),
        last_used: new Date(device.last_used).toLocaleString(),
        is_current: device.device_identifier === store.getters.getDeviceIdentifier,
      })).sort((a, b) => b.is_current ? 1 : -1),

    totalDevices: (state) => state.devices.length,
    getDeviceById: (state) => (id) =>
      state.devices.find((device) => device.device_id === id),
  },

  actions: {
    persistState() {
      setPersistedState(`deviceStore_${this.userId}`, {
        devices: this.devices,
      });
    },
    setUserId(userId) {
      this.userId = userId;
      this.reset();
      if (userId) {
        this.fetchDevices();
      }
    },
    reset() {
      if (this.userId) {
        localStorage.removeItem(`deviceStore_${this.userId}`);
      }
      this.$reset();
    },

    setError(err, fallbackMessage, fallbackCode) {
      this.error = {
        message: err?.message || fallbackMessage,
        code: err?.code || fallbackCode,
        timestamp: new Date().toISOString(),
      };
    },

    resetError() {
      this.error = null;
    },

    async fetchDevices() {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
        return [];
      }

      this.loading = true;
      this.resetError();

      try {
        const response = await apiCall({
          url: '/Device/loadDeviceList',
          method: 'GET',
        }, store);

        const data = response?.data || [];

        if (Array.isArray(data)) {
          this.devices = data;
          this.persistState();
          return data;
        }

        throw new Error(response?.message || 'Invalid response format');
      } catch (err) {
        this.setError(err, 'Failed to fetch devices', 'FETCH_DEVICES_FAILED');
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async toggleDeviceStatus(deviceId, status) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
      }
      this.loading = true;
      this.resetError();

      try {
        const response = await apiCall({
          method: 'POST',
          url: '/Device/toggleDeviceStatus',
          data: { deviceId, status },
        }, store);

        if (response.status === 'success') {
          await this.fetchDevices();
          return response;
        }

        throw new Error(response.message);
      } catch (err) {
        this.setError(err, 'Failed to change device status', 'STATUS_CHANGE_DEVICE_FAILED');
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async deleteDevice(deviceId) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
      }
      this.loading = true;
      this.resetError();

      try {
        const response = await apiCall({
          method: 'POST',
          url: '/Device/deleteDevice',
          data: { deviceId },
        }, store);

        if (response.status === 'success') {
          await this.fetchDevices();
          return response;
        }

        throw new Error(response.message);
      } catch (err) {
        this.setError(err, 'Failed to delete device', 'DELETE_DEVICE_FAILED');
        throw err;
      } finally {
        this.loading = false;
      }
    },
    async refreshDevices() {
      this.loading = true;
      try {
        await this.fetchDevices();
      } finally {
        this.loading = false;
      }
    },


  },
});

